// Conversion against occupancy through the day (stores with both sales and occupancy sensors).
// Occupancy: people in the store at every minute, left axis.
// Conversion rate: all purchases ÷ all entries since opening, right axis. Running totals
// keep the purchase lag (people pay 10-20 min after walking in) from distorting the line,
// and it ends at the day's rate shown in the panel. It starts at the first entry.
import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import { Chart as ChartJS } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import moment from 'moment';
import { applyChartTheme, sectionLineDataset, hoverGuidePlugin, CHART_THEME } from '../../Concession/charts/chartTheme';
import { PHASES, PURCHASE_COLOR } from '../retailPalette';

ChartJS.register(annotationPlugin);

const MIN_ENTRIES = 1; // first visitor onward (avoids dividing by zero)
const OCC_COLOR = '#3b82f6';

const dayMinute = (hhmm) => {
  if (!hhmm || !hhmm.includes(':')) return null;
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return (h < 3 ? h + 24 : h) * 60 + m;
};
const clock = (minute) => moment().startOf('day').add(minute, 'minutes').format('h:mm A');
const fmt = (n) => Math.round(n).toLocaleString();

// Same cut-off as the Traffic over time chart: stop once the count has been flat for 2 hours
const trimAfterFlatPeriod = (rows) => {
  let runStart = 0;
  for (let i = 1; i < rows.length; i += 1) {
    if (rows[i].count !== rows[runStart].count) runStart = i;
    else if (rows[i].m - rows[runStart].m >= 120) return rows.slice(0, i + 1);
  }
  return rows;
};

export default function ConversionOccupancyChart({ readings, salesIds, occupancy, maxOccupancy, currentGame, dayType, showPregame = true, showTitle = true }) {
  const { conversion, people, range } = useMemo(() => {
    const rows = trimAfterFlatPeriod((readings || [])
      .filter((r) => r && r.timestamp && r.timestamp !== '00:00' && !r.clear)
      .map((r) => ({
        m: dayMinute(r.timestamp.split('T')[1]?.slice(0, 5)),
        count: r.count || 0,
        purchases: salesIds.reduce((sum, id) => sum + (r[`${id}_out`] || 0), 0),
      }))
      .filter((r) => r.m != null)
      .sort((a, b) => a.m - b.m));
    if (rows.length < 2) return { conversion: [], people: [], range: null };
    const span = { min: rows[0].m, max: rows[rows.length - 1].m };

    const conv = rows
      .filter((row) => row.count >= MIN_ENTRIES)
      .map((row) => ({
        x: row.m,
        y: Math.round((row.purchases / row.count) * 1000) / 10,
        entries: row.count,
        purchases: row.purchases,
      }));

    // People in store at every minute (occupancy is per minute)
    const occ = [];
    if (occupancy && occupancy.length) {
      for (let m = span.min; m <= span.max; m += 1) {
        const t = occupancy[m]?.total;
        if (typeof t === 'number') occ.push({ x: m, y: t });
      }
    }
    return { conversion: conv, people: occ, range: span };
  }, [readings, salesIds, occupancy]);

  const isGame = dayType === 'game';
  const gates = isGame ? dayMinute(currentGame?.gatesOpen) : null;
  const start = isGame ? dayMinute(currentGame?.gameStartTime) : null;

  const hasPeople = people.length > 0;

  const options = useMemo(() => {
    const maxConv = Math.max(10, ...conversion.map((p) => p.y));
    const maxPeople = Math.max(maxOccupancy || 0, ...people.map((p) => p.y), 10);
    const band = (phase, xMin, xMax) => ({
      type: 'box',
      xScaleID: 'x',
      ...(xMin != null ? { xMin } : {}),
      ...(xMax != null ? { xMax } : {}),
      backgroundColor: phase.tint,
      borderWidth: 0,
      drawTime: 'beforeDatasetsDraw',
      label: {
        display: true,
        content: phase.label,
        position: { x: 'start', y: 'start' },
        color: phase.color,
        font: { size: 10, weight: '600', family: CHART_THEME.font },
        padding: { top: 4, left: 6 },
      },
    });
    const bands = isGame && start != null
      ? (gates != null
        ? [...(showPregame ? [band(PHASES.pregame, null, gates)] : []), band(PHASES.gates, gates, start), band(PHASES.game, start, null)]
        : [band(PHASES.game, start, null)])
      : [];

    const base = applyChartTheme({
      responsive: true,
      maintainAspectRatio: false,
      parsing: false,
      scales: {
        x: {
          type: 'linear',
          min: range ? range.min : undefined,
          max: range ? range.max : undefined,
          // A label on every hour (every 2 hours on long days) across the whole range
          afterBuildTicks: (axis) => {
            if (!range) return;
            const step = range.max - range.min > 10 * 60 ? 120 : 60;
            const ticks = [];
            for (let v = Math.ceil(range.min / step) * step; v <= range.max; v += step) ticks.push({ value: v });
            axis.ticks = ticks;
          },
          ticks: { autoSkip: false, maxRotation: 0, callback: (v) => clock(v).replace(':00', '') },
          grid: { display: false },
        },
        // People in store only when there's occupancy data to plot
        y: {
          display: hasPeople,
          position: 'left',
          beginAtZero: true,
          suggestedMax: maxPeople,
          title: { display: true, text: 'People in store', color: OCC_COLOR },
          ticks: { precision: 0 },
        },
        y1: {
          position: 'right',
          beginAtZero: true,
          suggestedMax: Math.ceil((maxConv + 5) / 5) * 5,
          title: { display: true, text: 'Conversion rate', color: PURCHASE_COLOR },
          ticks: { callback: (v) => `${v}%` },
          // Draws the gridlines itself when the people axis is hidden
          grid: { drawOnChartArea: !hasPeople },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => (items[0] ? clock(items[0].parsed.x) : ''),
            label: (item) => (item.dataset.yAxisID === 'y1'
              ? ` Conversion rate: ${item.raw.y}% (${fmt(item.raw.purchases)} purchases / ${fmt(item.raw.entries)} entries since opening)`
              : ` In store: ${fmt(item.raw.y)} people`),
          },
        },
        annotation: { annotations: bands },
      },
    });
    base.interaction = { mode: 'nearest', axis: 'x', intersect: false };
    return base;
  }, [conversion, people, hasPeople, range, maxOccupancy, isGame, gates, start, showPregame]);

  if (conversion.length < 2) return null;

  return (
    <div className="rx-conv-trend">
      <div className="rx-conv-trend-head">
        {showTitle && <span className="rx-subhead">{hasPeople ? 'Conversion and occupancy' : 'Conversion'}</span>}
        <span className="rx-conv-trend-keys">
          {hasPeople && <span><i style={{ background: OCC_COLOR }} />People in store</span>}
          <span><i style={{ background: PURCHASE_COLOR }} />Conversion rate</span>
        </span>
      </div>
      <div style={{ position: 'relative', height: 220 }}>
        <Line
          data={{
            datasets: [
              {
                label: 'Conversion rate',
                data: conversion,
                yAxisID: 'y1',
                ...sectionLineDataset(PURCHASE_COLOR),
                fill: false,
                borderWidth: 2.25,
                order: 1,
              },
              ...(hasPeople ? [{
                label: 'People in store',
                data: people,
                yAxisID: 'y',
                borderColor: 'rgba(59, 130, 246, 0.55)',
                backgroundColor: 'rgba(59, 130, 246, 0.14)',
                fill: 'origin',
                borderWidth: 1.5,
                pointRadius: 0,
                pointHoverRadius: 4,
                tension: 0.3,
                order: 2,
              }] : []),
            ],
          }}
          options={options}
          plugins={[hoverGuidePlugin]}
        />
      </div>
    </div>
  );
}
